import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
  index: number;
  isVisible: boolean;
}

const FAQItem: React.FC<FAQItemProps> = ({ 
  question, 
  answer, 
  isOpen, 
  onClick, 
  index, 
  isVisible 
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen, answer]);

  return (
    <div 
      className={`border-b border-border-light dark:border-border-dark transition-all duration-500 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 0.1 + 0.3}s` }}
    >
      <button
        className="flex items-center justify-between w-full py-5 text-left focus:outline-none"
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark pr-8">
          {question}
        </h3>
        <span className="flex-shrink-0 ml-4">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-primary-light dark:text-primary-dark" />
          ) : (
            <ChevronDown className="h-5 w-5 text-text-secondary-light dark:text-text-secondary-dark" />
          )}
        </span>
      </button>
      <div 
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: `${height}px` }}
      >
        <div className="pb-5 text-base text-text-secondary-light dark:text-text-secondary-dark">
          {answer}
        </div>
      </div>
    </div>
  );
};

export function PricingFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0); // First item open by default
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "What payment methods do you accept?",
      answer: (
        <div>
          <p>We accept <span className="text-text-primary-light dark:text-text-primary-dark">all major credit/debit cards, UPI, and net banking</span> through our secure payment processor. All transactions are encrypted and secure through <span className="text-text-primary-light dark:text-text-primary-dark">Secure Payment Gateway</span>.</p>
        </div>
      )
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: (
        <div>
          <p>Yes, you can cancel your subscription <span className="text-text-primary-light dark:text-text-primary-dark">any time</span>. Your subscription will remain active until the end of your current billing period. For non recurring plans such as Yearly Plan, your plan will be active until the end of the plan duration, but <span className="text-text-primary-light dark:text-text-primary-dark">no refund will be provided in case of cancellation</span>.</p>
        </div>
      )
    },
    {
      question: "Are the add-on bundles a one-time purchase?",
      answer: (
        <div>
          <p>Yes, add-on bundles are one-time purchases that give you additional interview minutes. Once purchased, these minutes remain in your account until you use them, otherwise they will <span className="text-text-primary-light dark:text-text-primary-dark">expire when your current plan expires</span>.</p>
        </div>
      )
    },
    {
      question: "What if my current plan expires but my minutes are still left?",
      answer: (
        <div>
          <p>Your left over minutes will always <span className="text-text-primary-light dark:text-text-primary-dark">be carried forward</span>. So, if there's no need to worry about your minutes expiring.</p>
        </div>
      )
    },
    {
      question: "Will I get a refund if I don't use all my interview minutes?",
      answer: (
        <div>
          <p>We don't offer refunds for any unused minutes, but your minutes will be carried forward to your next plan. You will soon be able to <span className="text-text-primary-light dark:text-text-primary-dark">gift your unused minutes to your friends and family</span> in future updates.</p>
        </div>
      )
    },
    {
      question: "How many mock interviews can I do in average?",
      answer: (
        <div>
          <p>The number of interviews depends on the length of each interview you conduct. On average <span className="text-text-primary-light dark:text-text-primary-dark">(for a 15-20 minute interview)</span>:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>60 minutes typically allows for 3-4 interviews</li>
            <li>120 minutes typically allows for 6-8 interviews</li>
            <li>Add-on bundles provide additional interviews based on their minute allocation</li>
          </ul>
        </div>
      )
    },
    {
        question: "Do you offer any discounts for students?",
        answer: (
          <div>
            <p>Yes, we offer special student discounts via <span className="text-text-primary-light dark:text-text-primary-dark">Coupon codes</span>. Please <span className="text-text-primary-light dark:text-text-primary-dark">contact our support team</span> to learn more about our academic pricing options.</p>
          </div>
        )
      },
    {
      question: "What is the Ambassador Program?",
      answer: (
        <div>
          <p>We offer a <span className="text-text-primary-light dark:text-text-primary-dark">Ambassador Program</span> for students, working professionals, and anyone who wants to help us reach out to more people and spread the word about Mocktrail. Ambassadors will be provided <span className="text-text-primary-light dark:text-text-primary-dark">monetary rewards & free interview minutes</span> for the number of people they refer and buy a plan from Mocktrail.</p>
        </div>
      )
    },
  ];

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto mb-12">
      <div className={`text-center mb-12 transform transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <h2 className="text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-text-primary-light to-primary-light dark:from-primary-dark dark:via-text-primary-dark dark:to-primary-dark">
          Frequently Asked Questions
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary-dark">
          Everything you need to know about our pricing and plans
        </p>
      </div>
      
      <div className="space-y-0 divide-y divide-border-light dark:divide-border-dark">
        {faqs.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onClick={() => toggleFAQ(index)}
            index={index}
            isVisible={isVisible}
          />
        ))}
      </div>
    </div>
  );
} 